import { FoundationElement, customElement, css, html, observable } from '@genesislcap/web-core';
import { BondSeries, MunisDeal } from './deal-detail.types';
import { dealInformationItem, labelStyles } from '../../../styles';
import { 
  getSeriesFieldValue, 
  getTradeDateValue, 
  getDateFieldValue,
  formatFieldOrBlank,
  formatDateOrBlank,
  getPurposeSectorDisplayValue,
  PurposeMapping,
} from './deal-detail.util';


@customElement({
  name: 'deal-detail-metadata',
  styles: css`
    .deal-information {
      display: flex;
      padding: 10px 10px;
      background: var(--black-color);
    }
    
    ${dealInformationItem}
    .deal-information-item {
      border-right: calc(var(--stroke-width) * 1px) solid var(--neutral-stroke-divider-rest);
    }

    .deal-information-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }

    .deal-information-item:last-child {
      border-right: none;
    }

    .deal-information-table {
      width: 100%;
      table-layout: fixed;
      border-spacing: 0 5px;

      th:first-child,
      td:first-child {
        width: 140px;
      }

      th {
        text-align: left;
      }
    }

    .deal-information-table-credit {
      th:first-child,
      td:first-child {
        width: 100px;
      }
    }

    ${labelStyles}
  `,
  template: html<DealDetailMetadata>`
    <div class="deal-information">
      <div class="deal-information-item">
        <h4>Issuer info</h4>
        <div class="deal-information-grid">
          <div class="deal-information-grid-item">
            <div class="label">Sector / Purpose</div>
            <div>${(x) => formatFieldOrBlank(getPurposeSectorDisplayValue(x.bondSeriesList, x.purposeMappings))}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">State</div>
            <div>${(x) => x.munisDeal.STATE || ''}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">CUSIP Root</div>
            <div>${(x) => x.munisDeal.CUSIP_ROOT || ''}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">Source of Repayment</div>
            <div>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'SOURCE_OF_REPAYMENT'))}</div>
          </div>
        </div>
      </div>
      <div class="deal-information-item">
        <h4>Offering info</h4>
        <div class="deal-information-grid">
          <div class="deal-information-grid-item">
            <div class="label">Tax Type</div>
            <div>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'TAX_STATUS'))}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">Interest Type</div>
            <div>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'INTEREST_TYPE'))}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">Issue Type</div>
            <div>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'SERIES_MONEY_TYPE'))}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">Security Type</div>
            <div>${(x) => getSeriesFieldValue(x.bondSeriesList, 'SECURITY_TYPE')}</div>
          </div>
        </div>
      </div>
      <div class="deal-information-item">
        <h4>Credit info</h4>
        <table class="deal-information-table deal-information-table-credit">
          <thead>
            <tr>
              <th></th>
              <th>Moody's</th>
              <th>S&P</th>
              <th>Fitch</th>
              <th>Kroll</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="label">Deal Rating</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'SERIES_MOODYS_RATING'))}</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'SERIES_SANDP_RATING'))}</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'SERIES_FITCH_RATING'))}</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'SERIES_KROLL_RATING'))}</td>
            </tr>
            <tr>
              <td class="label">Outlook</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'RATING_MOODYS_OUTLOOK'))}</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'RATING_SANDP_OUTLOOK'))}</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'RATING_FITCH_OUTLOOK'))}</td>
              <td>${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'RATING_KROLL_OUTLOOK'))}</td>
            </tr>
            <tr>
              <td class="label">Enhancement</td>
              <td colspan="4">${(x) => formatFieldOrBlank(getSeriesFieldValue(x.bondSeriesList, 'ENHANCEMENT'))}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="deal-information-item">
        <h4>Dates</h4>
        <div class="deal-information-grid">
          <div class="deal-information-grid-item">
            <div class="label">Dated Date</div>
            <div>${(x) => formatDateOrBlank(getDateFieldValue(x.bondSeriesList, 'DATED_DATE'), false)}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">Delivery Date</div>
            <div>${(x) => formatDateOrBlank(getDateFieldValue(x.bondSeriesList, 'DELIVERY_DATE'), false)}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">Award Date</div>
            <div>${(x) => formatDateOrBlank(getDateFieldValue(x.bondSeriesList, 'AWARD_DATE'), false)}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">Trade Date (Time)</div>
            <div>${(x) => {
              const tradeDateValue = getTradeDateValue(x.bondSeriesList);
              // If "Various", show as-is, otherwise format with time
              if (tradeDateValue === 'Various') {
                return 'Various';
              }
              return formatDateOrBlank(tradeDateValue, true);
            }}</div>
          </div>
          <div class="deal-information-grid-item">
            <div class="label">First Coupon Date</div>
            <div>${(x) => formatDateOrBlank(getDateFieldValue(x.bondSeriesList, 'FIRST_COUPON_DATE'), false)}</div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class DealDetailMetadata extends FoundationElement {
  @observable munisDeal: MunisDeal;
  @observable bondSeriesList: BondSeries[];
  @observable purposeMappings: PurposeMapping[] = [];
}
